import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarCheck, FaCheck, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { listApplications, listInterviewers, listInterviews, suggestSlots } from "./aiHireApi";
import { DAY_END_HOUR, DAY_START_HOUR, TODAY, WEEK_DAYS } from "./aiHireMockData";
import CandidateDrawer from "./CandidateDrawer";
import {
  AiScoreBadge,
  ChipGroup,
  FilterSelect,
  MatchTags,
  PreviewButton,
  Toolbar,
  fmtDate,
  fmtHour,
  useAiHireData,
} from "./AiHireUi";

const ROUNDS = ["Tech", "HR", "Client"];
const ROUND_TONE = { Tech: "indigo", HR: "teal", Client: "amber" };
const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i);
const ROW_PX = 48;
const VIEWS = [
  { id: "week", label: "Week" },
  { id: "day", label: "Day" },
  { id: "list", label: "List" },
];
const NARROW_QUERY = "(max-width: 1199.98px)";

function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(NARROW_QUERY).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW_QUERY);
    if (!mq) return undefined;
    const sync = () => setNarrow(mq.matches);
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);
  return narrow;
}

const placeOf = (v) => (v.mode === "Online" ? "Online" : v.interviewer.location);

async function loadWaiting() {
  const apps = await listApplications({ stages: ["shortlisted"] });
  return Promise.all(apps.map(async (app) => ({ app, slots: await suggestSlots(app.id) })));
}

function Block({ v, onOpen }) {
  return (
    <button
      type="button"
      className={`aihire-block aihire-tone--${ROUND_TONE[v.round]}${v.status === "Completed" ? " is-done" : ""}`}
      style={{ top: (v.start - DAY_START_HOUR) * ROW_PX, height: (v.end - v.start) * ROW_PX - 4 }}
      onClick={() => onOpen(v.application)}
      title={`${v.round} round · ${v.application.candidate.name} · ${v.interviewer.name} · ${v.place}`}
    >
      <b>
        {v.status === "Completed" ? <FaCheck aria-hidden="true" /> : null} {v.round} · {fmtHour(v.start)}
      </b>
      <span>{v.application.candidate.name}</span>
      <span>
        {v.interviewer.name} · {v.place}
      </span>
    </button>
  );
}

function Ghost({ slot }) {
  return (
    <div
      className="aihire-block aihire-block--ghost"
      style={{ top: (slot.start - DAY_START_HOUR) * ROW_PX, height: ROW_PX - 4 }}
      title="Suggested slot"
    >
      <b>Suggested · {fmtHour(slot.start)}</b>
      <span>
        {slot.interviewer.name} · {slot.place}
      </span>
    </div>
  );
}

function CalendarGrid({ days, interviews, ghosts, onOpen }) {
  return (
    <div className="aihire-cal" style={{ "--aihire-days": days.length, "--aihire-row": `${ROW_PX}px` }}>
      <div className="aihire-cal__corner" />
      {days.map((d) => (
        <div key={d.date} className={`aihire-cal__dayhead${d.date === TODAY ? " is-today" : ""}`}>
          {d.label}
        </div>
      ))}
      <div className="aihire-cal__hours">
        {HOURS.map((h) => (
          <span key={h} style={{ height: ROW_PX }}>
            {fmtHour(h)}
          </span>
        ))}
      </div>
      {days.map((d) => {
        const dayIndex = WEEK_DAYS.indexOf(d);
        return (
          <div key={d.date} className="aihire-cal__col" style={{ height: HOURS.length * ROW_PX }}>
            {ghosts
              .filter((g) => g.day === dayIndex)
              .map((g) => (
                <Ghost key={`${g.interviewer.id}-${g.start}`} slot={g} />
              ))}
            {interviews
              .filter((v) => v.day === dayIndex)
              .map((v) => (
                <Block key={v.id} v={v} onOpen={onOpen} />
              ))}
          </div>
        );
      })}
    </div>
  );
}

function InterviewList({ interviews, onOpen }) {
  return (
    <div className="aihire-daylist">
      {WEEK_DAYS.map((d, dayIndex) => {
        const list = interviews.filter((v) => v.day === dayIndex).sort((a, b) => a.start - b.start);
        return (
          <section key={d.date}>
            <h4 className={d.date === TODAY ? "is-today" : ""}>
              {d.label}
              {d.date === TODAY ? " · Today" : ""}
            </h4>
            {list.length === 0 ? <p className="aihire-muted">No interviews</p> : null}
            {list.map((v) => (
              <button key={v.id} type="button" className="aihire-listrow" onClick={() => onOpen(v.application)}>
                <b>
                  {fmtHour(v.start)}-{fmtHour(v.end)}
                </b>
                <span className={`aihire-chip aihire-tone--${ROUND_TONE[v.round]}`}>{v.round}</span>
                <span>
                  <strong>{v.application.candidate.name}</strong> · {v.application.job.title}
                </span>
                <span>
                  {v.interviewer.name} · {v.place}
                </span>
                <span className={v.status === "Completed" ? "aihire-done" : "aihire-muted"}>{v.status}</span>
              </button>
            ))}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Interview Scheduler: the week's interviews, plus shortlisted candidates waiting for a slot with
 * Ai Hire suggestions where their preferred location and time overlap an interviewer's free time.
 */
function InterviewSchedulerTab() {
  const interviews = useAiHireData(listInterviews, []);
  const waiting = useAiHireData(loadWaiting, []);
  const interviewers = useAiHireData(listInterviewers, []);
  const narrow = useNarrow();
  const [view, setView] = useState("week");
  const [dayIndex, setDayIndex] = useState(Math.max(0, WEEK_DAYS.findIndex((d) => d.date === TODAY)));
  const [location, setLocation] = useState("");
  const [round, setRound] = useState("");
  const [interviewerId, setInterviewerId] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(null);

  const filtered = useMemo(
    () =>
      (interviews || []).filter(
        (v) =>
          (!location || placeOf(v) === location) &&
          (!round || v.round === round) &&
          (!interviewerId || v.interviewerId === interviewerId)
      ),
    [interviews, location, round, interviewerId]
  );

  const effectiveView = narrow && view === "week" ? "list" : view;
  const ghosts = waiting?.find((w) => w.app.id === selected)?.slots || [];
  const days = effectiveView === "day" ? [WEEK_DAYS[dayIndex]] : WEEK_DAYS;

  return (
    <div className="aihire-tab">
      <Toolbar>
        <div className="aihire-weeknav">
          <PreviewButton icon={FaChevronLeft} small>
            Prev
          </PreviewButton>
          <PreviewButton small>Today</PreviewButton>
          <PreviewButton icon={FaChevronRight} small>
            Next
          </PreviewButton>
          <strong>Week of {fmtDate(WEEK_DAYS[0].date)}</strong>
        </div>
        <span className="aihire-toolbar__spacer" />
        <ChipGroup label="View" options={VIEWS} value={effectiveView} onChange={setView} />
      </Toolbar>
      <Toolbar>
        <FilterSelect label="Location" value={location} onChange={setLocation} options={["Hyderabad", "Bengaluru", "Online"]} />
        <FilterSelect label="Round" value={round} onChange={setRound} options={ROUNDS} />
        <FilterSelect
          label="Interviewer"
          value={interviewerId}
          onChange={setInterviewerId}
          options={(interviewers || []).map((i) => ({ value: i.id, label: `${i.name} (${i.location})` }))}
        />
        {effectiveView === "day" ? (
          <ChipGroup
            label="Day"
            options={WEEK_DAYS.map((d, i) => ({ id: String(i), label: d.label }))}
            value={String(dayIndex)}
            onChange={(v) => setDayIndex(Number(v))}
          />
        ) : null}
      </Toolbar>

      <div className="aihire-sched">
        <aside className="aihire-waiting">
          <h4>
            Waiting for a slot <em>{waiting?.length ?? 0}</em>
          </h4>
          <p className="aihire-muted">Pick a candidate to see Ai Hire&apos;s suggested slots on the calendar.</p>
          {(waiting || []).map(({ app, slots }) => (
            <div
              key={app.id}
              className={`aihire-waiting__card${selected === app.id ? " is-on" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={selected === app.id}
              onClick={() => setSelected(selected === app.id ? null : app.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(selected === app.id ? null : app.id);
                }
              }}
            >
              <div className="aihire-card__top">
                <b>{app.candidate.name}</b>
                <AiScoreBadge score={app.aiInterview?.score} recommendation={app.aiInterview?.recommendation} />
              </div>
              <div className="aihire-card__meta">
                {app.job.title} · lives in {app.candidate.location}
              </div>
              <div className="aihire-card__meta">
                Prefers {app.candidate.preferredLocations.join(" / ")} · {app.candidate.availability.label}
              </div>
              {slots.length === 0 ? (
                <p className="aihire-muted">No overlap this week. Ask the candidate for more times.</p>
              ) : (
                <ul className="aihire-slots">
                  {slots.map((s) => (
                    <li key={`${s.day}-${s.start}-${s.interviewer.id}`}>
                      <span>
                        <strong>
                          {s.dayLabel}, {fmtHour(s.start)}
                        </strong>{" "}
                        with {s.interviewer.name} · {s.place}
                      </span>
                      <MatchTags tags={s.tags} />
                    </li>
                  ))}
                </ul>
              )}
              <div className="aihire-waiting__actions">
                <PreviewButton icon={FaCalendarCheck} primary small>
                  Book slot
                </PreviewButton>
                <button
                  type="button"
                  className="aihire-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(app);
                  }}
                >
                  View profile
                </button>
              </div>
            </div>
          ))}
        </aside>

        <div className="aihire-sched__main">
          {effectiveView === "list" ? (
            <InterviewList interviews={filtered} onOpen={setOpen} />
          ) : (
            <CalendarGrid days={days} interviews={filtered} ghosts={ghosts} onOpen={setOpen} />
          )}
          <div className="aihire-legend">
            {ROUNDS.map((r) => (
              <span key={r} className={`aihire-chip aihire-tone--${ROUND_TONE[r]}`}>
                {r} round
              </span>
            ))}
            <span className="aihire-chip aihire-chip--ghost">Suggested slot</span>
            <span className="aihire-muted">Faded blocks are completed interviews.</span>
          </div>
        </div>
      </div>

      {open ? <CandidateDrawer app={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

export default InterviewSchedulerTab;
